import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  CreateBulkOrderApi,
  CreateBulkOrderRequest,
  CreateBulkOrderResponseInfo,
  DeleteOrderToOrderGroupRequest,
  GetOrderGroupResponseResult,
  OrderGroupApi,
  OrderToOrderGroupApi,
  PostOrderGroupRequest,
  PostOrderGroupResponseResult,
  PostOrderToOrderGroupRequest,
  PostOrderToOrderGroupResponseResult,
  PutOrderGroupRequest,
  UpdateOrderGroupResponseResult,
} from '../axios/generated/orderGroupManage';
import { IOrderGroupRepository } from '../interfaces/IOrderGroupRepository';
import { IError } from '../interfaces/IError';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';

/**
 * 発注グループ（まとめ発注）の永続化を行うリポジトリ
 */
export class OrderGroupRepository implements IOrderGroupRepository {
  /**
   * まとめ発注作成(発注グループ作成+発注作成)
   * @param bulkPostOrderRequest - リクエスト
   */
  async createBulkOrder(bulkPostOrderRequest: CreateBulkOrderRequest): Promise<INetworkResult<CreateBulkOrderResponseInfo, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const createBulkOrderApi = new CreateBulkOrderApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await createBulkOrderApi.createBulkOrder(bulkPostOrderRequest);

      switch (apiResponse.status) {
        case 200:
          const result: CreateBulkOrderResponseInfo = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループ取得
   * @param keyword - 検索キーワード(発注グループ名)
   */
  async getOrderGroup(keyword?: string): Promise<INetworkResult<GetOrderGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderGroupApi = new OrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderGroupApi.getOrderGroup(keyword);

      switch (apiResponse.status) {
        case 200:
          const result: GetOrderGroupResponseResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループ作成
   * @param postOrderGroupRequest - リクエスト
   */
  async postOrderGroup(postOrderGroupRequest: PostOrderGroupRequest): Promise<INetworkResult<PostOrderGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderGroupApi = new OrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderGroupApi.postOrderGroup(postOrderGroupRequest);

      switch (apiResponse.status) {
        case 200:
          const result: PostOrderGroupResponseResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループ編集
   * @param orderGroupId - 対象の発注グループID
   * @param putOrderGroupRequest - リクエスト
   */
  async updateOrderGroup(
    orderGroupId: string,
    putOrderGroupRequest: PutOrderGroupRequest
  ): Promise<INetworkResult<UpdateOrderGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderGroupApi = new OrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderGroupApi.putOrderGroup(orderGroupId, putOrderGroupRequest);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループ削除
   * @param orderGroupId - 削除する発注グループID
   * @returns - なし
   */
  async deleteOrderGroup(orderGroupId: string): Promise<INetworkResult<undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderGroupApi = new OrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderGroupApi.deleteOrderGroup(orderGroupId);

      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループに発注を紐づける処理
   * @param bulkPostOrderToOrderGroupRequest - 紐づける発注
   * @returns - 発注グループID
   */
  async postBulkOrderToOrderGroup(
    bulkPostOrderToOrderGroupRequest: PostOrderToOrderGroupRequest
  ): Promise<INetworkResult<PostOrderToOrderGroupResponseResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderToOrderGroupApi = new OrderToOrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderToOrderGroupApi.postOrderToOrderGroup(bulkPostOrderToOrderGroupRequest);

      switch (apiResponse.status) {
        case 200:
          const result: PostOrderToOrderGroupResponseResult = apiResponse.data['result'];
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }

  /**
   * 発注グループから発注を外す処理
   * @param bulkDeleteOrderToOrderGroupRequest - リクエスト
   */
  async deleteBulkOrderToOrderGroup(
    bulkDeleteOrderToOrderGroupRequest: DeleteOrderToOrderGroupRequest
  ): Promise<INetworkResult<undefined, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const orderToOrderGroupApi = new OrderToOrderGroupApi(undefined, undefined, axiosInstance);

    try {
      const apiResponse = await orderToOrderGroupApi.deleteOrderToOrderGroup(bulkDeleteOrderToOrderGroupRequest);

      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (err) {
      return { status: { type: 'error', error: axiosErrorHandler(err) } };
    }
  }
}
